import camelize from 'camelize';
import decamelizeKeys from 'decamelize-keys';

import { get, post, put } from 'helpers/http';

import * as Sentry from '@sentry/react';
import env from '../helpers/env';
import getLastUrlParam from '../helpers/getLastUrlParam';
import {
  MeetingTranslationActions,
  RealtimeTranscriptFetchActions,
  TranscriptActions
} from './actionTypes';

type TDispatch = (action: any) => void;
type TGetState = () => any;

interface ITranscriptFetchError {
  message?: unknown;
  name?: string;
  status?: number;
}

const TRANSCRIPT_FETCH_ERROR_FALLBACK =
  'Unable to fetch the transcript. Please try again later.';
const NO_TRANSCRIPT_URL_ERROR = '[avoma] no transcriptUrl provided';
const NO_TRANSCRIPTION_ID_ERROR = '[avoma] no transcriptionId provided';
const NO_TRANSCRIPT_UUID_ERROR = '[avoma] no transcriptUuid provided';
const NO_MEETING_ID_ERROR = '[avoma] no meetingId provided';

let replaceExistingSnapshotSequence = 0;

export const getNextReplaceExistingSnapshotSequence = () => {
  replaceExistingSnapshotSequence += 1;
  return replaceExistingSnapshotSequence;
};

/** @internal test helper */
export const resetReplaceExistingSnapshotSequenceForTests = () => {
  replaceExistingSnapshotSequence = 0;
};

const stringifyTranscriptFetchError = (value: unknown) => {
  if (typeof value === 'string') {
    return value;
  }

  if (value == null) {
    return '';
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const normalizeTranscriptFetchError = (error: ITranscriptFetchError) => {
  const { message } = error;

  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  if (message && typeof message === 'object') {
    const { detail } = message as { detail?: unknown };
    const detailMessage = stringifyTranscriptFetchError(detail);

    if (detailMessage) {
      return detailMessage;
    }
  }

  return error.name || TRANSCRIPT_FETCH_ERROR_FALLBACK;
};

const captureTranscriptFetchFailure = ({
  error,
  errorMessage,
  transcriptionId,
  transcriptionUrl
}: {
  error: ITranscriptFetchError;
  errorMessage: string;
  transcriptionId: string;
  transcriptionUrl: string;
}) => {
  Sentry.withScope(scope => {
    scope.setContext('transcript_fetch_failure', {
      transcriptionId,
      transcriptionUrl,
      status: error.status,
      name: error.name,
      normalizedMessage: errorMessage,
      originalMessageType: typeof error.message,
      originalMessageKeys:
        error.message && typeof error.message === 'object'
          ? Object.keys(error.message)
          : undefined
    });

    Sentry.captureException(
      new Error(`[avoma] Transcript fetch failed: ${errorMessage}`)
    );
  });
};

export const fetchTranscription =
  (transcriptionUrl: string, shouldClearEditorState = false) =>
  (dispatch: TDispatch) => {
    if (!transcriptionUrl) throw Error(NO_TRANSCRIPT_URL_ERROR);
    const transcriptionId = getLastUrlParam(transcriptionUrl);
    dispatch({ type: 'FETCH_TRANSCRIPTION_REQUEST', transcriptionId });

    return get(transcriptionUrl)
      .then(transcription => {
        dispatch({
          type: 'FETCH_TRANSCRIPTION_SUCCESS',
          transcription,
          shouldClearEditorState
        });
      })
      .catch(err => {
        const error = (err || {}) as ITranscriptFetchError;
        const errorMessage = normalizeTranscriptFetchError(error);

        captureTranscriptFetchFailure({
          error,
          errorMessage,
          transcriptionId,
          transcriptionUrl
        });

        dispatch({
          type: 'FETCH_TRANSCRIPTION_FAILURE',
          transcriptionId,
          error: errorMessage
        });
        throw err;
      });
  };

export const triggerTranslation =
  (transcriptUuid: string, meetingUuid: string, body: any) =>
  async (dispatch: TDispatch) => {
    if (!transcriptUuid) throw Error(NO_TRANSCRIPT_UUID_ERROR);

    dispatch({
      type: MeetingTranslationActions.FETCH_MEETING_TRANSLATION,
      meetingUuid
    });

    const url = `${env.avomaApiUrl}/api/v1/transcripts/${transcriptUuid}/translate/`;

    try {
      await post(url, decamelizeKeys(body));
    } catch (e) {
      dispatch({
        type: MeetingTranslationActions.FETCH_MEETING_TRANSLATION_ERROR,
        meetingUuid
      });

      // Re throw the error so that the caller can handle it in the UI if needed
      throw e;
    }
  };

export const optimisticallyUpdateTranslationCounts =
  (transcriptUuid: string, language: any) =>
  (dispatch: TDispatch, getState: TGetState) => {
    if (!transcriptUuid) throw Error(NO_TRANSCRIPT_UUID_ERROR);

    const state = getState();

    const existingCountData =
      state.transcriptions.data[transcriptUuid].translationCount;

    // The meeting is already translated in this language. Don't update the count
    if (
      existingCountData.notesTranslatedLanguages?.includes(
        language.languageCode.toLowerCase()
      )
    ) {
      return;
    }

    const newCountData = {
      transcriptTranslation: existingCountData.transcriptTranslation + 1,
      notesTranslation: existingCountData.notesTranslation + 1,
      notesTranslatedLanguages: [
        ...existingCountData.notesTranslatedLanguages,
        language.languageCode
      ],
      transcriptTranslatedLanguages: [
        ...existingCountData.transcriptTranslatedLanguages,
        language.languageCode
      ]
    };

    dispatch({
      type: TranscriptActions.OPTIMISTIC_UPDATE_FOR_TRANSLATION_COUNT,
      transcriptionId: transcriptUuid,
      translationCountData: { translationCount: newCountData }
    });
  };

export const fetchRealTimeTranscription =
  (
    meetingId: string,
    options?: {
      replaceExisting?: boolean;
    }
  ) =>
  async (dispatch: TDispatch) => {
    if (!meetingId) throw Error(NO_MEETING_ID_ERROR);

    const url = `${env.avomaApiUrl}/transcription/rtt_transcript/${meetingId}/`;
    const snapshotSequence = options?.replaceExisting
      ? getNextReplaceExistingSnapshotSequence()
      : undefined;

    try {
      const transcripts = (await get(url)) as any[];

      dispatch({
        type: RealtimeTranscriptFetchActions.FETCH_EXISTING_REALTIME_TRANSCRIPTION_SUCCESS,
        existingTranscripts: transcripts.map((transcript: any) =>
          camelize(transcript.rtt_transcript)
        ),
        replaceExisting: options?.replaceExisting ?? false,
        snapshotSequence
      });
    } catch {
      // Desktop live-sync polling uses this fetch as a backup to websocket
      // updates. Preserve the current transcript state and retry on the next tick.
    }
  };

export const saveEditedTranscript =
  ({ editedTranscript, uuid }: { editedTranscript: any; uuid: string }) =>
  (dispatch: TDispatch, getState: TGetState) => {
    const state = getState();
    if (!uuid) throw Error('[avoma] no transcription uuid provided');
    if (
      !state.transcriptions ||
      !state.transcriptions.data ||
      !state.transcriptions.data[uuid]
    ) {
      throw Error(
        '[avoma] tried to save transcription with uuid that doesnt exist'
      );
    }

    dispatch({
      type: 'SAVE_EDITED_TRANSCRIPT_REQUEST',
      transcriptionId: uuid
    });

    const { recording } = state.transcriptions.data[uuid];

    put(`${env.avomaApiUrl}/api/v1/transcripts/${uuid}/`, {
      edited_transcript: editedTranscript,
      uuid,
      recording
    })
      .then(transcription => {
        dispatch({
          type: 'SAVE_EDITED_TRANSCRIPT_SUCCESS',
          transcription
        });
      })
      .catch(err => {
        dispatch({
          type: 'SAVE_EDITED_TRANSCRIPT_FAILURE',
          error: err.message,
          transcriptionId: uuid
        });
        throw err;
      });
  };

export const setTranscriptionEditorState =
  (transcriptionId: string, editorState: any) =>
  (dispatch: TDispatch, getState: TGetState) => {
    if (!transcriptionId) throw Error(NO_TRANSCRIPTION_ID_ERROR);

    if (
      getState().app.noteAndTranscriptLinkedAt &&
      editorState.getSelection().getHasFocus()
    ) {
      // if we're linked && this isn't the initial editor state being set
      dispatch({ type: 'CLEAR_NOTE_AND_TRANSCRIPT_LINKED' });
    }
    dispatch({
      type: 'SET_TRANSCRIPTION_EDITOR_STATE',
      transcriptionId,
      editorState
    });
  };

export const setTranscriptionSearchResults =
  (transcriptionId: string, matches: any, keyword: string) =>
  (dispatch: TDispatch) => {
    if (!transcriptionId) throw Error(NO_TRANSCRIPTION_ID_ERROR);

    dispatch({
      type: 'SET_TRANSCRIPTION_SEARCH_RESULTS',
      transcriptionId,
      matches,
      keyword
    });
  };

export const setTranscriptionSearchQueue =
  (transcriptionId: string, keyword: string) => (dispatch: TDispatch) => {
    if (!transcriptionId) throw Error(NO_TRANSCRIPTION_ID_ERROR);

    dispatch({
      type: 'SET_TRANSCRIPTION_SEARCH_QUEUE',
      transcriptionId,
      keyword
    });
  };

export const setErrorParsingTranscript =
  (transcriptionId: string, error: unknown) => (dispatch: TDispatch) => {
    if (!transcriptionId) {
      return;
    }
    dispatch({
      type: 'SET_ERROR_PARSING_TRANSCRIPT',
      transcriptionId,
      error
    });
  };

export const setScrolling =
  (scrolling: boolean) => (dispatch: TDispatch, getState: TGetState) => {
    if (getState().transcriptions.scrolling !== scrolling) {
      dispatch({
        type: 'SET_SCROLLING',
        scrolling
      });
    }
  };

export const setScroller = (scroller: any) => (dispatch: TDispatch) => {
  dispatch({
    type: TranscriptActions.SET_SCROLLER,
    scroller
  });
};

export const clearBlur = () => (dispatch: TDispatch) => {
  dispatch({
    type: TranscriptActions.BLUR_EDITOR,
    blur: false
  });
};

export const saveScrollTop =
  (meetingId: string | null, scrollTime: number) => (dispatch: TDispatch) => {
    dispatch({
      type: TranscriptActions.SAVE_SCROLL_TOP,
      meetingId,
      scrollTime
    });
  };

export const clearScrollTop = () => (dispatch: TDispatch) => {
  dispatch({
    type: TranscriptActions.SAVE_SCROLL_TOP,
    meetingId: null,
    scrollTime: 0
  });
};

export const autoScrollOn = () => (dispatch: TDispatch) => {
  dispatch({
    type: TranscriptActions.AUTO_SCROLLER_ON
  });
};

export const autoScrollOff = () => (dispatch: TDispatch) => {
  dispatch({
    type: TranscriptActions.AUTO_SCROLLER_OFF
  });
};
